import { Button } from '@deliveryhero/pd-cookbook/components/ButtonV2';
import { Link } from '@deliveryhero/pd-cookbook/components/Link';
import { Pill } from '@deliveryhero/pd-cookbook/components/Pill';
import { Tag, TagVariant } from '@deliveryhero/pd-cookbook/components/Tag';
import { Typography } from '@deliveryhero/pd-cookbook/components/Typography';
import {
  Alert,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select
} from '@mui/material';

import { Checkbox } from 'components/Checkbox';
import { TierCard } from 'components/Subscription/TierCard';
import { TierReviewSummary } from 'components/Subscription/TierReviewSummary';
import { SelectionType } from 'components/Subscription/types';
import { Card } from 'src/components/Card';
import HighlightAsterisks from 'src/components/HighlightAsterisks/HighlightAsterisks';
import { getCountryAppDomain } from 'src/utils/get-country-app-domain';
import isFoodora from 'src/utils/is-foodora/is-foodora';

import { SubscribePageProps } from './types';

export const SubscribePage = ({
  config,
  selectionType,
  setSelectionType,
  departments,
  tiers,
  selectedDepartments,
  selectedEmployees,
  selectedTierIndex,
  employeeCountToSubscribe,
  amountBilled,
  setSelectedTierIndex,
  setSelectedDepartments,
  onSelectEmployeesClick
}: SubscribePageProps) => {
  const allEmployeesSelected =
    selectedDepartments.length === departments?.length;
  const isFoodoraPro = isFoodora();

  return (
    <div style={{ display: 'flex', gap: '16px', flexDirection: 'column' }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography type="title-md">
            <HighlightAsterisks text="Select a plan *" />
          </Typography>

          {!isFoodoraPro && (
            <Typography type="label-md">
              <Link
                href={`${getCountryAppDomain(
                  config.code
                )}/contents/corporate-pandapro`}
                target="_blank"
              >
                Learn about plans
              </Link>
            </Typography>
          )}
        </div>

        <Grid item container spacing={2} aria-label="subscription plans">
          {tiers.map((tier, idx) => (
            <Grid item key={idx} xs={true}>
              <TierCard
                currencyCode={config.currencyCode}
                tier={tier}
                selectedValue={selectedTierIndex}
                value={idx}
                onClick={() => {
                  setSelectedTierIndex(idx);
                }}
              />
            </Grid>
          ))}
        </Grid>
      </Card>

      <Card>
        <Grid container spacing={2}>
          <Grid item>
            <Typography type="title-md">
              <HighlightAsterisks text="Select employees *" />
            </Typography>
          </Grid>
          <Grid item container spacing={2}>
            <Grid item>
              <Pill
                id="by-departments"
                checked={selectionType === SelectionType.Department}
                label="By departments"
                onChange={() => setSelectionType(SelectionType.Department)}
              />
            </Grid>
            <Grid item>
              <Pill
                id="by-individuals"
                checked={selectionType === SelectionType.Employee}
                label="By individuals"
                onChange={() => setSelectionType(SelectionType.Employee)}
              />
            </Grid>
          </Grid>
          <Grid item xs={12}>
            {selectionType === SelectionType.Department && (
              <FormControl fullWidth>
                <InputLabel
                  id="select-departments-label"
                  sx={{
                    textTransform: 'capitalize',
                    '&.MuiInputLabel-shrink': {
                      color: '#E21B70',
                      backgroundColor: 'white'
                    }
                  }}
                >
                  Departments
                </InputLabel>
                <Select
                  labelId="select-departments-label"
                  multiple
                  value={selectedDepartments}
                  onChange={(e) => {
                    const {
                      target: { value }
                    } = e;

                    value.length && (value as number[]).includes(-1)
                      ? setSelectedDepartments(
                          allEmployeesSelected
                            ? []
                            : departments?.map((d) => d.id)
                        )
                      : setSelectedDepartments(
                          value as typeof selectedDepartments
                        );
                  }}
                  renderValue={() =>
                    allEmployeesSelected
                      ? `All departments selected (${selectedDepartments.length})`
                      : `${departments
                          .filter((department) =>
                            selectedDepartments.includes(department.id)
                          )
                          .map((filteredDepartment) => filteredDepartment.name)
                          .join(', ')}`
                  }
                  multiline
                  sx={{
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#E21B70'
                    },
                    width: '100%'
                  }}
                >
                  <MenuItem key={-1} value={-1}>
                    <Checkbox checked={allEmployeesSelected} />
                    All Departments
                  </MenuItem>
                  {departments.map((department) => (
                    <MenuItem key={department.id} value={department.id}>
                      <Checkbox
                        checked={selectedDepartments.includes(department.id)}
                      />
                      {department.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {selectionType === SelectionType.Employee &&
              (selectedEmployees.length ? (
                selectedEmployees.map((employee) => (
                  <Tag
                    key={employee.id}
                    id={employee.id.toString()}
                    label={employee.name}
                    variant={TagVariant.ELEVATED}
                  />
                ))
              ) : (
                <Button variant="text" onClick={onSelectEmployeesClick}>
                  + Select Employees
                </Button>
              ))}
          </Grid>
          <Grid item xs={12}>
            <Alert
              severity="info"
              sx={{
                color: 'neutral.primary',
                typography: 'paragraphSm',
                backgroundColor: '#EEF0F1',
                '.MuiAlert-icon': { color: 'neutral.primary' }
              }}
            >
              Only <b>active employees that are not subscribed</b> to{' '}
              {isFoodoraPro ? 'foodora PRO' : 'pandapro'} on their corporate
              accounts are listed here.
            </Alert>
          </Grid>
        </Grid>
      </Card>

      <Card>
        <TierReviewSummary
          tier={tiers[selectedTierIndex]}
          employeeCountToSubscribe={employeeCountToSubscribe}
          amountBilled={amountBilled}
          currencyCode={config.currencyCode}
        />
      </Card>
    </div>
  );
};
